import { useEffect, useRef, useState } from 'react';

interface UseIntersectionObserverProps {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
  enabled?: boolean;
}

/**
 * This hook is used to observe the intersection of an element with the viewport.
 * This is used to trigger a callback when the specified element is visible in the window viewport.
 * @param options - The options for the intersection observer.
 * @returns The intersection observer state and the element reference.
 */
const useIntersectionObserver = (options: UseIntersectionObserverProps) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const hasTriggeredRef = useRef(false);
  const elementRef = useRef<HTMLDivElement>(null);

  const { threshold = 0.1, rootMargin = '0px', triggerOnce = true, enabled = true } = options;

  useEffect(() => {
    const element = elementRef.current;
    if (!element || !enabled) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const isVisible = entry.isIntersecting;

        if (isVisible && triggerOnce && !hasTriggeredRef.current) {
          setIsIntersecting(true);
          hasTriggeredRef.current = true;
          observer.unobserve(element);
        } else if (!triggerOnce) {
          setIsIntersecting(isVisible);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => observer.unobserve(element);
  }, [triggerOnce, threshold, rootMargin, enabled]);

  return { isIntersecting, ref: elementRef };
};

export default useIntersectionObserver;
